import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Users, Plus, Edit, Trash2, Shield, User, 
  Search, UtensilsCrossed, MapPin, Check, X,
  Eye, EyeOff, Ban, ExternalLink, Building2, Settings,
  Clock, UserCheck, UserX, AlertCircle
} from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import { SurveyManagement } from "@/components/SurveyManagement";
import NotificationsPanel from "@/components/NotificationsPanel";
import LandingImagesCard from "@/components/LandingImagesCard";
import LandingContentManager from "@/components/LandingContentManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, token, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  // Helper for translated messages
  const getAdminMsg = (key) => {
    const msgs = {
      errorSavingUser: { en: "Error saving user", es: "Error al guardar usuario", pt: "Erro ao salvar usuário", ht: "Erè nan anrejistre itilizatè" },
      errorDeletingUser: { en: "Error deleting user", es: "Error al eliminar usuario", pt: "Erro ao excluir usuário", ht: "Erè nan efase itilizatè" },
      errorUpdatingUser: { en: "Error updating user", es: "Error al actualizar usuario", pt: "Erro ao atualizar usuário", ht: "Erè nan mete ajou itilizatè" },
      errorLoadingMenus: { en: "Error loading menus", es: "Error al cargar menús", pt: "Erro ao carregar menus", ht: "Erè nan chajman meni" },
      errorLoadingLocations: { en: "Error loading locations", es: "Error al cargar ubicaciones", pt: "Erro ao carregar localizações", ht: "Erè nan chajman kote" },
      errorSuspendingUser: { en: "Error suspending user", es: "Error al suspender usuario", pt: "Erro ao suspender usuário", ht: "Erè nan sispann itilizatè" },
      errorReactivating: { en: "Error reactivating service", es: "Error al reactivar servicio", pt: "Erro ao reativar serviço", ht: "Erè nan reaktive sèvis" },
    };
    return msgs[key]?.[language] || msgs[key]?.en || key;
  };
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Users list
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // All menus and locations for detail views
  const [allMenus, setAllMenus] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [showMenusDialog, setShowMenusDialog] = useState(false);
  const [showLocationsDialog, setShowLocationsDialog] = useState(false);
  const [menuSearchTerm, setMenuSearchTerm] = useState("");
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  
  // Create/Edit user dialog
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    is_active: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete confirmation
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Suspend dialog
  const [suspendUser, setSuspendUser] = useState(null);
  const [suspensionMessage, setSuspensionMessage] = useState("");

  // System settings
  const [systemSettings, setSystemSettings] = useState({ max_file_size_mb: 25, max_video_size_mb: 25 });
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ max_file_size_mb: 25, max_video_size_mb: 25 });

  // Pending users approval
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [processingUserId, setProcessingUserId] = useState(null);

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        const [usersRes, statsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { max_file_size_mb: 25, max_video_size_mb: 25 } }))
        ]);
        setUsers(usersRes.data);
        setStats(statsRes.data);
        setSystemSettings(settingsRes.data);
        setSettingsForm(settingsRes.data);
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error(t('adminAccessRequired'));
          navigate("/dashboard");
        } else {
          const errorMsg = {
            en: "Error loading data",
            es: "Error al cargar datos",
            pt: "Erro ao carregar dados",
            ht: "Erè nan chajman done"
          };
          toast.error(errorMsg[language] || errorMsg.en);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, t]);

  // Fetch pending users
  const fetchPendingUsers = async () => {
    if (!token) return;
    setIsLoadingPending(true);
    try {
      const response = await axios.get(`${API}/admin/users/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(response.data);
    } catch (error) {
      console.error("Error fetching pending users:", error);
    } finally {
      setIsLoadingPending(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Approve user
  const handleApproveUser = async (userId) => {
    setProcessingUserId(userId);
    try {
      await axios.post(
        `${API}/admin/users/${userId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('userApproved') || (language === 'en' ? "User approved successfully" : language === 'pt' ? "Usuário aprovado com sucesso" : language === 'ht' ? "Itilizatè apwouve avèk siksè" : "Usuario aprobado exitosamente"));
      // Refresh both lists
      fetchPendingUsers();
      // Re-fetch all users to update the main list
      const usersRes = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? "Error approving user" : language === 'pt' ? "Erro ao aprovar usuário" : language === 'ht' ? "Erè nan apwobasyon itilizatè" : "Error al aprobar usuario"));
    } finally {
      setProcessingUserId(null);
    }
  };

  // Reject user
  const handleRejectUser = async (userId) => {
    setProcessingUserId(userId);
    try {
      await axios.post(
        `${API}/admin/users/${userId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('userRejected') || (language === 'en' ? "User rejected and removed" : language === 'pt' ? "Usuário rejeitado e removido" : language === 'ht' ? "Itilizatè rejte epi retire" : "Usuario rechazado y eliminado"));
      // Refresh pending users list
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? "Error rejecting user" : language === 'pt' ? "Erro ao rejeitar usuário" : language === 'ht' ? "Erè nan rejè itilizatè" : "Error al rechazar usuario"));
    } finally {
      setProcessingUserId(null);
    }
  };

  // Save system settings
  const handleSaveSettings = async () => {
    try {
      const response = await axios.put(
        `${API}/admin/settings`,
        settingsForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSystemSettings(response.data);
      setIsSettingsDialogOpen(false);
      const successMsg = {
        en: "Configuration saved",
        es: "Configuración guardada",
        pt: "Configuração salva",
        ht: "Konfigirasyon anrejistre"
      };
      toast.success(successMsg[language] || successMsg.en);
    } catch (error) {
      const errorMsg = {
        en: "Error saving configuration",
        es: "Error al guardar configuración",
        pt: "Erro ao salvar configuração",
        ht: "Erè nan anrejistre konfigirasyon"
      };
      toast.error(errorMsg[language] || errorMsg.en);
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Open create dialog
  const handleOpenCreate = () => {
    setEditingUser(null);
    setUserForm({
      name: "",
      email: "",
      password: "",
      role: "user",
      is_active: true
    });
    setShowPassword(false);
    setIsUserDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (userToEdit) => {
    setEditingUser(userToEdit);
    setUserForm({
      name: userToEdit.name,
      email: userToEdit.email,
      password: "",
      role: userToEdit.role || "user",
      is_active: userToEdit.is_active !== false
    });
    setShowPassword(false);
    setIsUserDialogOpen(true);
  };

  // Save user
  const handleSaveUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) {
      toast.error(t('fillAllFields'));
      return;
    }
    
    if (!editingUser && !userForm.password) {
      toast.error(t('passwordRequired'));
      return;
    }
    
    setIsSaving(true);
    try {
      if (editingUser) {
        // Update
        const updateData = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          is_active: userForm.is_active
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        
        await axios.put(
          `${API}/admin/users/${editingUser.id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setUsers(users.map(u => 
          u.id === editingUser.id 
            ? { ...u, ...updateData, password: undefined }
            : u
        ));
        toast.success(t('userUpdated'));
      } else {
        // Create
        const response = await axios.post(
          `${API}/admin/users`,
          userForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUsers([...users, response.data]);
        
        // Update stats
        setStats(prev => prev ? {
          ...prev,
          total_users: prev.total_users + 1,
          active_users: prev.active_users + 1
        } : null);
        
        toast.success(t('userCreated'));
      }
      
      setIsUserDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || getAdminMsg('errorSavingUser'));
    } finally {
      setIsSaving(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(
        `${API}/admin/users/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsers(users.filter(u => u.id !== userToDelete.id));
      
      // Update stats
      setStats(prev => prev ? {
        ...prev,
        total_users: prev.total_users - 1,
        active_users: userToDelete.is_active !== false ? prev.active_users - 1 : prev.active_users
      } : null);
      
      toast.success(t('userDeleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || getAdminMsg('errorDeletingUser'));
    } finally {
      setUserToDelete(null);
    }
  };

  // Toggle user active status
  const handleToggleActive = async (userToToggle) => {
    try {
      const newStatus = !userToToggle.is_active;
      await axios.put(
        `${API}/admin/users/${userToToggle.id}`,
        { is_active: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsers(users.map(u => 
        u.id === userToToggle.id ? { ...u, is_active: newStatus } : u
      ));
      
      // Update stats
      setStats(prev => prev ? {
        ...prev,
        active_users: newStatus ? prev.active_users + 1 : prev.active_users - 1
      } : null);
      
      toast.success(newStatus ? t('userActivated') : t('userDeactivated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || getAdminMsg('errorUpdatingUser'));
    }
  };

  // Open menus dialog
  const handleOpenMenus = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-menus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllMenus(response.data);
      setShowMenusDialog(true);
    } catch (error) {
      toast.error(getAdminMsg('errorLoadingMenus'));
    }
  };

  // Open locations dialog
  const handleOpenLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllLocations(response.data);
      setShowLocationsDialog(true);
    } catch (error) {
      toast.error(getAdminMsg('errorLoadingLocations'));
    }
  };

  // Suspend user
  const handleSuspendUser = async () => {
    if (!suspendUser) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${suspendUser.id}`,
        { 
          is_suspended: true,
          suspension_message: suspensionMessage || "Este servicio está temporalmente suspendido."
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsers(users.map(u => 
        u.id === suspendUser.id 
          ? { ...u, is_suspended: true, suspension_message: suspensionMessage }
          : u
      ));
      
      toast.success(t('userSuspended'));
      setSuspendUser(null);
      setSuspensionMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || getAdminMsg('errorSuspendingUser'));
    }
  };

  // Reactivate user service
  const handleReactivateService = async (userToReactivate) => {
    try {
      await axios.put(
        `${API}/admin/users/${userToReactivate.id}`,
        { is_suspended: false, suspension_message: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsers(users.map(u => 
        u.id === userToReactivate.id 
          ? { ...u, is_suspended: false, suspension_message: "" }
          : u
      ));
      
      toast.success(t('serviceReactivated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || getAdminMsg('errorReactivating'));
    }
  };

  // Filter menus
  const filteredMenus = allMenus.filter(m => 
    m.name?.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
    m.owner_name?.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
    m.owner_email?.toLowerCase().includes(menuSearchTerm.toLowerCase())
  );

  // Filter locations
  const filteredLocations = allLocations.filter(l => 
    l.name?.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
    l.owner_name?.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
    l.address?.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-500">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/dashboard")}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('back')}
            </Button>
            <div className="flex items-center gap-3">
              <img 
                src="/assets/logo-sally.png" 
                alt="Sally Better" 
                className="h-8 w-auto invert"
              />
              <div className="flex items-center gap-2 border-l border-stone-300 pl-3">
                <Shield className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-stone-900">{t('adminPanel')}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-600">{user?.name}</span>
            <Badge variant="secondary" className="bg-amber-100 text-amber-800">
              Admin
            </Badge>
            <LanguageSelector variant="dark" showLabel={false} />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Notifications Panel */}
        <NotificationsPanel token={token} language={language} />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-default">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">{t('totalUsers')}</p>
                <p className="text-2xl font-bold text-stone-900">{stats?.total_users || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-default">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <User className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">{t('activeUsers')}</p>
                <p className="text-2xl font-bold text-stone-900">{stats?.active_users || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow hover:border-purple-300"
            onClick={handleOpenMenus}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <UtensilsCrossed className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">{t('totalMenus')}</p>
                <p className="text-2xl font-bold text-stone-900">{stats?.total_menus || 0}</p>
                <p className="text-xs text-purple-600">{t('clickToViewAll')}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow hover:border-orange-300"
            onClick={handleOpenLocations}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <MapPin className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">{t('totalLocations')}</p>
                <p className="text-2xl font-bold text-stone-900">{stats?.total_locations || 0}</p>
                <p className="text-xs text-orange-600">{t('clickToViewAll')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Settings Card */}
        <Card className="mb-8">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-stone-600" />
                <CardTitle className="text-lg">Configuración del Sistema</CardTitle>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsSettingsDialogOpen(true)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-xs text-stone-500 mb-1">Tamaño máximo de imágenes</p>
                <p className="text-lg font-semibold">{systemSettings.max_file_size_mb || 25} MB</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-xs text-stone-500 mb-1">Tamaño máximo de videos</p>
                <p className="text-lg font-semibold">{systemSettings.max_video_size_mb || 25} MB</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Management Card */}
        <Card className="mb-8 border-blue-200 bg-gradient-to-br from-blue-50/50 to-indigo-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-lg text-blue-900">
                  {language === 'es' ? 'Gestión de Clientes' : 'Client Management'}
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/admin/clients")}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="open-clients-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Abrir Clientes' : 'Open Clients'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-blue-700 mb-1">
              {language === 'es' ? 'Registra clientes, asigna códigos únicos y gestiona tu base de datos.' : 'Register clients, assign unique codes and manage your database.'}
            </p>
          </CardContent>
        </Card>

        {/* Invoices Card */}
        <Card className="mb-8 border-orange-200 bg-gradient-to-br from-orange-50/50 to-amber-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-orange-600" />
                <CardTitle className="text-lg text-orange-900">
                  {language === 'es' ? 'Cotizaciones & Facturas' : 'Quotes & Invoices'}
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/admin/invoices")}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="open-invoices-btn"
              >
                {language === 'es' ? 'Abrir Facturas' : 'Open Invoices'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-orange-700 mb-1">
              {language === 'es' ? 'Crea facturas, exporta a PDF, imprime y envía por correo.' : 'Create invoices, export PDF, print and send by email.'}
            </p>
          </CardContent>
        </Card>

        {/* Coupons Card */}
        <Card className="mb-8 border-purple-200 bg-gradient-to-br from-purple-50/50 to-pink-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-purple-600" />
                <CardTitle className="text-lg text-purple-900">
                  {language === 'es' ? 'Cupones de Descuento' : 'Discount Coupons'}
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/admin/coupons")}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="open-coupons-btn"
              >
                {language === 'es' ? 'Abrir Cupones' : 'Open Coupons'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-purple-700 mb-1">
              {language === 'es' ? 'Crea cupones de descuento para tus clientes y registra canjes.' : 'Create discount coupons for your clients and track redemptions.'}
            </p>
          </CardContent>
        </Card>

        {/* Products Card */}
        <Card className="mb-8 border-rose-200 bg-gradient-to-br from-rose-50/50 to-pink-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-rose-600" />
                <CardTitle className="text-lg text-rose-900">
                  {language === 'es' ? 'Productos & Servicios' : 'Products & Services'}
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/admin/products")}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="open-products-btn"
              >
                {language === 'es' ? 'Abrir Productos' : 'Open Products'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-rose-700 mb-1">
              {language === 'es' ? 'Administra tu catálogo de productos y servicios para pedidos y facturas.' : 'Manage your product and service catalog for orders and invoices.'}
            </p>
          </CardContent>
        </Card>

        {/* Orders Card */}
        <Card className="mb-8 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-violet-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-indigo-600" />
                <CardTitle className="text-lg text-indigo-900">
                  {language === 'es' ? 'Pedidos & Seguimiento' : 'Orders & Tracking'}
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/admin/orders")}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="open-orders-btn"
              >
                {language === 'es' ? 'Abrir Pedidos' : 'Open Orders'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-indigo-700 mb-1">
              {language === 'es' ? 'Crea pedidos, actualiza estados y los clientes reciben notificaciones automáticas.' : 'Create orders, update status and clients receive automatic notifications.'}
            </p>
          </CardContent>
        </Card>

        {/* Financials Card */}
        <Card className="mb-8 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-green-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-emerald-600" />
                <CardTitle className="text-lg text-emerald-900">
                  {language === 'es' ? 'Reporte Financiero' : 'Financial Report'}
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/admin/financials")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="open-financials-btn"
              >
                {language === 'es' ? 'Abrir Reporte' : 'Open Report'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-emerald-700 mb-1">
              {language === 'es'
                ? 'Revisa gastos, ingresos y ganancia por pedido. Filtra por día, semana, mes o año.'
                : 'Review expenses, revenue and profit per order. Filter by day, week, month or year.'}
            </p>
          </CardContent>
        </Card>

        {/* Landing Page Editor Card */}
        <Card className="mb-8 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-teal-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-emerald-600" />
                <CardTitle className="text-lg text-emerald-900">Editor de Landing Page</CardTitle>
              </div>
              <Button 
                size="sm"
                onClick={() => navigate("/admin/landing-editor")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Edit className="w-4 h-4 mr-2" />
                Abrir Editor
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-emerald-700 mb-3">
              Personaliza la página principal: textos, imágenes, colores y orden de secciones.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-white/70 px-2 py-1 rounded">
                <Check className="w-3 h-3" /> Textos
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-white/70 px-2 py-1 rounded">
                <Check className="w-3 h-3" /> Imágenes
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-white/70 px-2 py-1 rounded">
                <Check className="w-3 h-3" /> Colores
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-white/70 px-2 py-1 rounded">
                <Check className="w-3 h-3" /> Reordenar
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-white/70 px-2 py-1 rounded">
                <Check className="w-3 h-3" /> Ocultar
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-white/70 px-2 py-1 rounded">
                <Check className="w-3 h-3" /> Historial
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Landing Images Card */}
        <LandingImagesCard />

        {/* Portfolio, Print thumbnails, Call schedule */}
        <LandingContentManager />

        {/* Settings Dialog */}
        <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración del Sistema
              </DialogTitle>
              <DialogDescription>
                Configura los límites de tamaño de archivos para todo el sistema.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tamaño máximo de imágenes (MB)</Label>
                <Select 
                  value={String(settingsForm.max_file_size_mb)} 
                  onValueChange={(v) => setSettingsForm(prev => ({ ...prev, max_file_size_mb: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 25, 30, 40, 50].map(size => (
                      <SelectItem key={size} value={String(size)}>{size} MB</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tamaño máximo de videos (MB)</Label>
                <Select 
                  value={String(settingsForm.max_video_size_mb)} 
                  onValueChange={(v) => setSettingsForm(prev => ({ ...prev, max_video_size_mb: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 25, 30, 40, 50, 75, 100].map(size => (
                      <SelectItem key={size} value={String(size)}>{size} MB</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-stone-500 bg-amber-50 p-3 rounded-lg">
                ⚠️ Archivos muy grandes pueden afectar el rendimiento. Recomendado: 25MB para imágenes, 25-50MB para videos.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsSettingsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveSettings}>
                Guardar Configuración
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <SurveyManagement token={token} language={language} />

        {/* Pending Users Approval Section */}
        {pendingUsers.length > 0 && (
          <Card className="mb-8 border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2 text-amber-800">
                      <AlertCircle className="w-4 h-4" />
                      {language === 'en' ? 'Pending Approvals' : 'Aprobaciones Pendientes'}
                    </CardTitle>
                    <CardDescription className="text-amber-700">
                      {language === 'en' 
                        ? `${pendingUsers.length} user(s) waiting for approval` 
                        : `${pendingUsers.length} usuario(s) esperando aprobación`}
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-amber-500 text-white">
                  {pendingUsers.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingUsers.map((pendingUser) => (
                  <div 
                    key={pendingUser.id} 
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200 shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{pendingUser.name}</p>
                        <p className="text-sm text-stone-500">{pendingUser.email}</p>
                        <p className="text-xs text-stone-400">
                          {language === 'en' ? 'Registered:' : 'Registrado:'} {new Date(pendingUser.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleRejectUser(pendingUser.id)}
                        disabled={processingUserId === pendingUser.id}
                        data-testid={`reject-user-${pendingUser.id}`}
                      >
                        {processingUserId === pendingUser.id ? (
                          <span className="animate-pulse">...</span>
                        ) : (
                          <>
                            <UserX className="w-4 h-4 mr-1" />
                            {language === 'en' ? 'Reject' : 'Rechazar'}
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => handleApproveUser(pendingUser.id)}
                        disabled={processingUserId === pendingUser.id}
                        data-testid={`approve-user-${pendingUser.id}`}
                      >
                        {processingUserId === pendingUser.id ? (
                          <span className="animate-pulse">...</span>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4 mr-1" />
                            {language === 'en' ? 'Approve' : 'Aprobar'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Management */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t('userManagement')}
              </CardTitle>
              <CardDescription>{t('manageAllUsers')}</CardDescription>
            </div>
            <Button onClick={handleOpenCreate} className="bg-stone-900 hover:bg-stone-800">
              <Plus className="w-4 h-4 mr-2" />
              {t('addUser')}
            </Button>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder={t('searchUsers')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Users Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{t('name')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{t('email')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{t('role')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{t('status')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{t('created')}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-stone-600">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-stone-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center">
                            {u.role === "admin" ? (
                              <Shield className="w-4 h-4 text-amber-600" />
                            ) : (
                              <User className="w-4 h-4 text-stone-600" />
                            )}
                          </div>
                          <div>
                            <span className="font-medium text-stone-900">{u.name}</span>
                            {u.is_suspended && (
                              <Badge className="ml-2 bg-red-100 text-red-700" variant="secondary">
                                <Ban className="w-3 h-3 mr-1" />
                                {t('suspended')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-stone-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <Badge 
                          variant={u.role === "admin" ? "default" : "secondary"}
                          className={u.role === "admin" ? "bg-amber-100 text-amber-800" : ""}
                        >
                          {u.role === "admin" ? "Admin" : "Usuario"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge 
                          variant={u.is_active !== false ? "default" : "destructive"}
                          className={u.is_active !== false ? "bg-green-100 text-green-800" : ""}
                        >
                          {u.is_active !== false ? t('active') : t('inactive')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-stone-500 text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Suspend/Reactivate Service button */}
                          {u.is_suspended ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReactivateService(u)}
                              disabled={u.id === user?.id}
                              title={t('reactivateService')}
                              className="text-green-600 hover:bg-green-50"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSuspendUser(u); setSuspensionMessage(""); }}
                              disabled={u.id === user?.id}
                              title={t('suspendService')}
                              className="text-orange-600 hover:bg-orange-50"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(u)}
                            disabled={u.id === user?.id}
                            title={u.is_active !== false ? t('deactivate') : t('activate')}
                          >
                            {u.is_active !== false ? (
                              <EyeOff className="w-4 h-4 text-stone-500" />
                            ) : (
                              <Eye className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(u)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUserToDelete(u)}
                            disabled={u.id === user?.id}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-stone-500">
                        {searchTerm ? t('noResults') : t('noUsersYet')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit User Dialog */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? t('editUser') : t('addUser')}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? t('editUserDesc') : t('addUserDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="Juan Pérez"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="juan@ejemplo.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label>
                {t('password')} {editingUser && <span className="text-stone-400">({t('leaveBlank')})</span>}
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder={editingUser ? "••••••••" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('role')}</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(v) => setUserForm({ ...userForm, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4" /> Usuario
                    </span>
                  </SelectItem>
                  <SelectItem value="admin">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Administrador
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {editingUser && (
              <div className="flex items-center justify-between">
                <Label>{t('accountActive')}</Label>
                <Switch
                  checked={userForm.is_active}
                  onCheckedChange={(checked) => setUserForm({ ...userForm, is_active: checked })}
                  disabled={editingUser?.id === user?.id}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUserDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSaveUser} 
              disabled={isSaving}
              className="bg-stone-900 hover:bg-stone-800"
            >
              {isSaving ? "..." : (editingUser ? t('save') : t('create'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {t('confirmDeleteUser')}
            </DialogTitle>
            <DialogDescription>
              {t('confirmDeleteUserDesc')} <strong>{userToDelete?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm text-stone-600">
            {t('deleteUserWarning')}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserToDelete(null)}>
              {t('cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              <Trash2 className="w-4 h-4 mr-2" />
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend User Dialog */}
      <Dialog open={!!suspendUser} onOpenChange={(open) => !open && setSuspendUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Ban className="w-5 h-5" />
              {t('suspendService')}
            </DialogTitle>
            <DialogDescription>
              {t('suspendServiceDesc')} <strong>{suspendUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>{t('suspensionMessage')}</Label>
              <Textarea
                value={suspensionMessage}
                onChange={(e) => setSuspensionMessage(e.target.value)}
                placeholder={t('suspensionMessagePlaceholder')}
                rows={3}
              />
              <p className="text-xs text-stone-500">{t('suspensionMessageHelp')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendUser(null)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSuspendUser} className="bg-orange-600 hover:bg-orange-700">
              <Ban className="w-4 h-4 mr-2" />
              {t('suspend')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Menus Dialog */}
      <Dialog open={showMenusDialog} onOpenChange={setShowMenusDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UtensilsCrossed className="w-5 h-5" />
              {t('allMenus')} ({allMenus.length})
            </DialogTitle>
            <DialogDescription>{t('allMenusDesc')}</DialogDescription>
          </DialogHeader>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder={t('searchMenus')}
              value={menuSearchTerm}
              onChange={(e) => setMenuSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-stone-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('menuName')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('owner')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('items')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('updated')}</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-stone-600">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {filteredMenus.map((menu) => (
                  <tr key={menu.id} className="hover:bg-stone-50">
                    <td className="px-4 py-2 font-medium text-stone-900">{menu.name}</td>
                    <td className="px-4 py-2">
                      <div className="text-sm">
                        <p className="text-stone-900">{menu.owner_name}</p>
                        <p className="text-stone-500 text-xs">{menu.owner_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-stone-600">{menu.items?.length || 0}</td>
                    <td className="px-4 py-2 text-stone-500 text-sm">
                      {menu.updated_at ? new Date(menu.updated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/menu/${menu.id}`, '_blank')}
                          title={t('viewPublic')}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setShowMenusDialog(false); navigate(`/editor/${menu.id}`); }}
                          title={t('edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredMenus.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-stone-500">
                      {menuSearchTerm ? t('noResults') : t('noMenusYet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* All Locations Dialog */}
      <Dialog open={showLocationsDialog} onOpenChange={setShowLocationsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {t('allLocations')} ({allLocations.length})
            </DialogTitle>
            <DialogDescription>{t('allLocationsDesc')}</DialogDescription>
          </DialogHeader>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder={t('searchLocations')}
              value={locationSearchTerm}
              onChange={(e) => setLocationSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-stone-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('locationName')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('owner')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('address')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-stone-600">{t('menus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {filteredLocations.map((location) => (
                  <tr key={location.id} className="hover:bg-stone-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-orange-500" />
                        <span className="font-medium text-stone-900">{location.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-sm">
                        <p className="text-stone-900">{location.owner_name}</p>
                        <p className="text-stone-500 text-xs">{location.owner_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-stone-600 text-sm">
                      {location.address || '-'}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="secondary">{location.menu_count} {t('menus')}</Badge>
                    </td>
                  </tr>
                ))}
                {filteredLocations.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-stone-500">
                      {locationSearchTerm ? t('noResults') : t('noLocationsYet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
